import React, { useEffect, useState } from 'react';
import RecipeCard from './RecipeCard';
import Masonry from 'react-masonry-css';
import { Spinner, Collapse } from 'react-bootstrap';
import { usePantryRecipes } from '../hooks/usePantryRecipes';
import { useGetAllFavoriteRecipes } from '../hooks/favoriteRecipe';
import { useGetAllDislikedRecipes } from '../hooks/dislikeRecipe';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAuth } from '../context/AuthContext';
import ErrorBoundary from './ErrorBoundary';
import { getIngredientImageUrl } from '../utils/imageUtils';
import AddPantryIngredientButton from './AddPantryIngredientButton';

const RecipesForPantry = ({ searchTerm = '', onMealAdded }) => {
    const { fetchPantryRecipes, pantryRecipes, isLoading, error } = usePantryRecipes();
    const { getAllFavoriteRecipes } = useGetAllFavoriteRecipes();
    const { getAllDislikedRecipes } = useGetAllDislikedRecipes();
    const [favoriteRecipes, setFavoriteRecipes] = useState([]);
    const [dislikedRecipes, setDislikedRecipes] = useState([]);
    const [pantryIngredients, setPantryIngredients] = useState(null);
    const { sessionToken, pgUser } = useAuth();
    
    // Track which sections are expanded (start with first one open)
    const [expandedSections, setExpandedSections] = useState({});

    const breakpointColumns = {
        default: 4,
        1100: 3,
        700: 2,
        400: 1
    };

    // Fetch pantry recipes
    useEffect(() => {
        if (sessionToken) {
            fetchPantryRecipes();
        }
    }, [sessionToken, fetchPantryRecipes]);

    // Toggle section expansion
    const toggleSection = (ingredientId) => {
        setExpandedSections(prev => ({
            ...prev,
            [ingredientId]: !prev[ingredientId]
        }));
    };

    // Fetch favorite and disliked recipes
    useEffect(() => {
        if (!sessionToken) return;
        
        const fetchFavoritesAndDislikes = async () => {
            try {
                // Fetch favorites
                const favoritesData = await getAllFavoriteRecipes(sessionToken);
                if (Array.isArray(favoritesData)) {
                    setFavoriteRecipes(favoritesData);
                }

                // Fetch dislikes
                const dislikesData = await getAllDislikedRecipes(sessionToken);
                if (Array.isArray(dislikesData)) {
                    setDislikedRecipes(dislikesData);
                }
            } catch (error) {
                console.error('Error fetching favorites/dislikes:', error);
            }
        };
        fetchFavoritesAndDislikes();
    }, [sessionToken, getAllFavoriteRecipes, getAllDislikedRecipes]);

    // Fetch pantry ingredients for pantry match calculation
    useEffect(() => {
        const fetchPantryIngredientsForMatch = async () => {
            if (!sessionToken || !pgUser?.pantry_id || !pgUser?.id) return;
            
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(data);
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
            }
        };

        fetchPantryIngredientsForMatch();
    }, [sessionToken, pgUser?.pantry_id, pgUser?.id]);

    // Filter recipes based on search term
    const getFilteredRecipes = (recipes) => {
        if (!searchTerm) return recipes;
        
        const searchLower = searchTerm.toLowerCase();
        return recipes.filter(recipe => {
            // Search by recipe name
            if (recipe.recipe_name?.toLowerCase().includes(searchLower)) {
                return true;
            }
            
            // Search by description
            if (recipe.description?.toLowerCase().includes(searchLower)) {
                return true;
            }
            
            return false;
        });
    };

    // Filter pantry recipes sections based on search term
    const getFilteredPantryRecipes = () => {
        if (!searchTerm) return pantryRecipes;
        
        return pantryRecipes.map(item => ({
            ...item,
            recipes: getFilteredRecipes(item.recipes || [])
        })).filter(item => item.recipes.length > 0);
    };

    const filteredPantryRecipes = getFilteredPantryRecipes();

    if (isLoading) {
        return (
            <div className="text-center my-4">
                <Spinner animation="border" role="status" style={{ color: '#52cd92' }}>
                    <span className="visually-hidden">Loading pantry recipes...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <p className="mb-0">Error loading recipes: {error}</p>
            </div>
        );
    }

    const handlePantryIngredientAdded = async () => {
        // Refetch pantry recipes when a new ingredient is added
        if (sessionToken) {
            await fetchPantryRecipes();
        }
        
        // Refetch pantry ingredients for pantry match calculation
        if (sessionToken && pgUser?.pantry_id && pgUser?.id) {
            try {
                const response = await fetchWithCSRF(`/api/v1/pantry-ingredient/pantry/${pgUser.pantry_id}/app_user/${pgUser.id}`, {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (Array.isArray(data)) {
                        setPantryIngredients(data);
                    }
                }
            } catch (error) {
                console.error('Error fetching pantry ingredients:', error);
            }
        }
    };

    return (
        <ErrorBoundary fallbackMessage="Unable to load pantry recipes. Please try again later.">
            <div className="container">
                <div className="d-flex align-items-center" style={{ paddingLeft: '15px', paddingRight: '15px', marginBottom: '15px', marginTop: '25px', flexWrap: 'wrap', gap: '10px' }}>
                    <h2 style={{ marginRight: '0px', marginBottom: '0px' }}>Recipes Based on Your Pantry</h2>
                    {pgUser?.pantry_id && pgUser?.id && (
                        <AddPantryIngredientButton
                            pantryId={pgUser.pantry_id}
                            appUserId={pgUser.id}
                            onSubmit={handlePantryIngredientAdded}
                            buttonText="Add Ingredient to Pantry"
                        />
                    )}
                </div>
                {/* <p style={{ textAlign: 'left', paddingLeft: '15px' }}>
                    Discover recipes you can make with ingredients you already have
                </p> */}
                
                {pantryRecipes.length === 0 ? (
                    <div className="alert alert-info" role="alert">
                        <p className="mb-0">No pantry ingredients found. Add ingredients to your pantry to see recipe suggestions!</p>
                    </div>
                ) : filteredPantryRecipes.length === 0 && searchTerm ? (
                    <div className="text-center my-5">No recipes found matching "{searchTerm}"</div>
                ) : (
                    <div className="recipes-by-ingredient">
                        {filteredPantryRecipes.map((item) => {
                            if (!item.recipes || item.recipes.length === 0) return null;
                            
                            const isExpanded = expandedSections[item.ingredient_id];
                            
                            return (
                                <div key={item.ingredient_id} className="ingredient-section mb-4">
                                    {/* Ingredient Header - Clickable */}
                                    <div 
                                        className="ingredient-header d-flex align-items-center justify-content-between"
                                        onClick={() => toggleSection(item.ingredient_id)}
                                        style={{ 
                                            paddingLeft: '15px',
                                            paddingRight: '15px',
                                            paddingTop: '12px',
                                            paddingBottom: '12px',
                                            gap: '12px',
                                            cursor: 'pointer',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            transition: 'background-color 0.2s',
                                            border: '1px solid #e0e0e0'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#e9ecef';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        }}
                                    >
                                        <div className="d-flex align-items-center" style={{ gap: '12px', flex: 1 }}>
                                            <img 
                                                src={getIngredientImageUrl(item.ingredient_id)}
                                                alt={item.ingredient_name}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    borderRadius: '8px',
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => { 
                                                    e.target.onerror = null; 
                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIGZpbGw9IiNFMEUwRTAiLz48cGF0aCBkPSJNMTggMjVIMjVWMzBIMzNDMzQuNjU3IDMwIDM1Ljc4MSAyOC44NzYgMzUuNzgxIDI3VjIzQzM1Ljc4MSAyMS4zNDMgMzQuNjU3IDIwIDMzIDIwSDI1VjI1SDE4WiIgZmlsbD0iIzkwOTA5MCIvPjwvc3ZnPg=='; 
                                                }}
                                            />
                                            <div style={{ textAlign: 'left' }}>
                                                <h3 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                                    Recipes with {item.ingredient_name}
                                                </h3>
                                                <small className="text-muted" style={{ display: 'block' }}>
                                                    {item.recipes.length} recipe{item.recipes.length !== 1 ? 's' : ''}
                                                </small>
                                            </div>
                                        </div>
                                        
                                        {/* Chevron Icon */}
                                        <svg 
                                            width="24" 
                                            height="24" 
                                            viewBox="0 0 24 24" 
                                            fill="none"
                                            style={{
                                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s'
                                            }}
                                        >
                                            <path 
                                                d="M6 9L12 15L18 9" 
                                                stroke="currentColor" 
                                                strokeWidth="2" 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>

                                    {/* Collapsible Recipes Grid */}
                                    <Collapse in={isExpanded}>
                                        <div className="mt-3">
                                            <Masonry
                                                breakpointCols={breakpointColumns}
                                                className="my-masonry-grid"
                                                columnClassName="my-masonry-grid_column"
                                            >
                                                {item.recipes.map(recipe => (
                                                    <RecipeCard 
                                                        key={recipe.recipe_id} 
                                                        recipe={{
                                                            id: recipe.recipe_id,
                                                            name: recipe.recipe_name,
                                                            description: recipe.description,
                                                            prep_time: recipe.prep_time,
                                                            cook_time: recipe.cook_time,
                                                            total_time: recipe.total_time,
                                                            servings: recipe.servings,
                                                            difficulty: recipe.difficulty,
                                                            instructions: recipe.instructions,
                                                            image_url: recipe.image_url,
                                                            is_vegetarian: recipe.is_vegetarian,
                                                            is_vegan: recipe.is_vegan,
                                                            protein: recipe.protein,
                                                            carbohydrate: recipe.carbohydrate,
                                                            fat: recipe.fat,
                                                            calories: recipe.calories,
                                                            micronutrients: recipe.micronutrients,
                                                            cost_per_serving: recipe.cost_per_serving,
                                                            taste_profile: recipe.taste_profile,
                                                            tags: recipe.tags,
                                                            equipment: recipe.equipment,
                                                            created_at: recipe.created_at,
                                                            updated_at: recipe.updated_at,
                                                            ingredients: recipe.ingredients || []
                                                        }}
                                                        isFavorite={Array.isArray(favoriteRecipes) && favoriteRecipes.includes(Number(recipe.recipe_id))}
                                                        isDisliked={Array.isArray(dislikedRecipes) && dislikedRecipes.includes(Number(recipe.recipe_id))}
                                                        pantryIngredients={pantryIngredients}
                                                        onMealAdded={onMealAdded}
                                                    />
                                                ))}
                                            </Masonry>
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

export default RecipesForPantry;

